(() => {
  'use strict';
  if (customElements.get('pd-contact-phone')) return;
  const key = 'Телефон за връзка';
  const markerKey = 'pd-phone-prefill-v3';
  let draft;
  let saved;
  let signature;
  let saveQueue = Promise.resolve();
  let pending = false;
  let finishing = false;
  let timer;
  const replay = new WeakSet();
  const fields = () => Array.from(document.querySelectorAll('pd-contact-phone'));
  const valid = value => /^\+?[\d\s().-]+$/.test(value) && /^\d{7,15}$/.test(value.replace(/\D/g, ''));
  const root = () => window.Shopify?.routes?.root || '/';
  function normalize(value) {
    const compact = value.trim().replace(/[\s().-]/g, '');
    if (compact.startsWith('00')) return '+' + compact.slice(2);
    if (compact.startsWith('+')) return compact;
    if (compact.startsWith('0')) return '+359' + compact.slice(1);
    if (/^359\d{7,9}$/.test(compact)) return '+' + compact;
    if (/^[89]\d{8}$/.test(compact)) return '+359' + compact;
    return compact;
  }
  function status(text, invalid = false) {
    fields().forEach(field => {
      field.querySelector('[data-phone-status]').textContent = text;
      field.input.setAttribute('aria-invalid', String(invalid));
    });
  }
  async function digest(value) {
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
  }
  function persist(value) {
    const normalized = normalize(value);
    const valueToSave = valid(normalized) ? normalized : '';
    const operation = saveQueue.catch(() => {}).then(async () => {
      const response = await fetch(root() + 'cart/update.js', {
        method: 'POST', headers: {'Content-Type':'application/json', Accept:'application/json'},
        body: JSON.stringify({attributes:{[key]:valueToSave, 'Order Type':''}})
      });
      if (!response.ok) throw new Error('Phone update failed');
      const cart = await response.json();
      if ((cart.attributes?.[key] || '') !== valueToSave) throw new Error('Phone was not persisted');
      saved = valueToSave;
      signature = valueToSave ? await digest(cart.token.split('?')[0] + '|' + valueToSave) : undefined;
    });
    saveQueue = operation;
    return operation;
  }
  class ContactPhone extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="tel"]');
      if (!this.input || this.bound) return;
      this.bound = true;
      if (draft === undefined) draft = this.input.value;
      this.input.value = draft;
      this.input.addEventListener('input', () => {
        draft = this.input.value;
        fields().forEach(field => { if (field !== this && field.input) field.input.value = draft; });
        this.input.setCustomValidity('');
        status('');
        clearTimeout(timer);
        timer = setTimeout(() => persist(draft).catch(() => status(this.dataset.saveError)), 350);
      });
      this.input.addEventListener('invalid', () => {
        status(this.dataset.invalid, true);
        this.input.setCustomValidity(this.dataset.invalid);
      });
    }
  }
  customElements.define('pd-contact-phone', ContactPhone);
  function findField(control) {
    return fields().find(field => field.input?.form === control.form);
  }
  async function intercept(event, control) {
    if (replay.has(control)) { replay.delete(control); return; }
    const field = findField(control);
    if (!field || !control.form) {
      event.preventDefault(); event.stopImmediatePropagation();
      location.assign(root() + 'cart#pd-contact-phone-cart');
      return;
    }
    const value = normalize(field.input.value);
    if (event.type === 'submit' && !pending && valid(value) && saved === value) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (pending) return;
    clearTimeout(timer);
    if (!valid(value)) {
      status(field.dataset.invalid, true);
      field.input.setCustomValidity(field.dataset.invalid);
      field.input.focus(); field.input.reportValidity();
      persist('').catch(() => status(field.dataset.saveError, true));
      return;
    }
    pending = true;
    fields().forEach(f => f.input.readOnly = true);
    status(field.dataset.saving);
    try {
      draft = value;
      fields().forEach(f => f.input.value = value);
      await persist(value);
      status('');
      field.input.setCustomValidity('');
      pending = false;
      fields().forEach(f => f.input.readOnly = false);
      replay.add(control);
      if (event.type === 'click') control.click();
      else event.target.requestSubmit(control);
    } catch (_) {
      status(field.dataset.saveError, true); field.input.focus();
    } finally {
      pending = false;
      fields().forEach(f => f.input.readOnly = false);
    }
  }
  window.addEventListener('click', event => {
    const control = event.target.closest?.('[name="checkout"],a[href="/checkout"],a[href$="/checkout"]');
    if (control) intercept(event, control);
  }, true);
  window.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') intercept(event, event.submitter);
  }, true);
  window.addEventListener('submit', async event => {
    if (event.defaultPrevented || event.submitter?.name !== 'checkout') return;
    const field = findField(event.submitter);
    if (!field || !signature || saved !== normalize(field.input.value)) return;
    event.preventDefault();
    if (finishing) return;
    finishing = true;
    fields().forEach(f => f.input.readOnly = true);
    status(field.dataset.saving);
    try {
      await saveQueue;
      const response = await fetch(root() + 'cart/update.js', {
        method: 'POST', headers: {Accept:'application/json'}, body: new FormData(event.target)
      });
      if (!response.ok) throw new Error('Cart update failed');
      const cart = await response.json();
      if (cart.attributes?.[key] !== saved) throw new Error('Phone was not persisted');
      signature = await digest(cart.token.split('?')[0] + '|' + saved);
      let previous;
      try { previous = sessionStorage.getItem(markerKey); } catch (_) {}
      const url = new URL(root() + 'checkout', location.origin);
      if (previous !== signature) {
        url.searchParams.set('checkout[shipping_address][phone]', saved);
        try { sessionStorage.setItem(markerKey, signature); } catch (_) {}
      }
      location.assign(url.href);
    } catch (_) {
      finishing = false;
      fields().forEach(f => f.input.readOnly = false);
      status(field.dataset.saveError, true);
      field.input.focus();
    }
  });
  window.addEventListener('pageshow', () => {
    pending = false;
    finishing = false;
    fields().forEach(field => field.input.readOnly = false);
    status('');
  });
})();
